import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { CompanyDetailsFooter } from '@/components/CompanyDetailsFooter';
import { S3_BUCKET_URL } from '@/constants';
import { amplitudeModule } from '@/modules/amplitude';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import {
  OptionItemSprite,
  OptionItemSpriteList,
} from '@/pages/Onboarding/components/OptionItemSprite/OptionItemSprite';
import { useOnboarding } from '@/pages/Onboarding/context';
import {
  OwnAiTitle,
  OwnAiTitleHighlight,
} from '@/pages/Onboarding/Layouts/OwnAI/components/Title';
import { OnbOptionType } from '@/pages/Onboarding/types';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';

import { LayoutDataType } from '../../types';

import { ETHNICITY_OPTIONS_FEMALE, ETHNICITY_OPTIONS_MALE } from './constants';
import { ScreenEthnicityStyles } from './Ethnicity.styles';

const getFemaleImageSrc = (ethnicityId: string, age: string) =>
  `${S3_BUCKET_URL}/layout-own-ai/ethnicity/${ethnicityId}/sprite-${age}.png`;

const getMaleImageSrc = (ethnicityId: string, age: string) =>
  `${S3_BUCKET_URL}/layout-own-ai-man/ethnicity/sprite-${age}-${ethnicityId}.png`;

export const ScreenEthnicity: React.FC = () => {
  const { wizard } = useOnboarding();
  const form = useFormContext<LayoutDataType>();
  const ethnicity = form.watch('ethnicity');
  const age = form.watch('age') ?? '18-24';
  const gender = form.watch('gender');

  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Ethnicity',
      });
    }
  }, []);

  const handleSelect = (option: OnbOptionType<LayoutDataType['ethnicity']>) => {
    form.setValue('ethnicity', option.id);
    userService.createOrUpdate({
      onboarding_data: {
        ethnicity: option.title,
      },
    });
    nextStepTimeout(wizard.nextStep);
  };

  const ethnicityOptions =
    gender === 'male' ? ETHNICITY_OPTIONS_MALE : ETHNICITY_OPTIONS_FEMALE;

  const getImageSrc = (ethnicityId: string) =>
    gender === 'male'
      ? getMaleImageSrc(ethnicityId, age)
      : getFemaleImageSrc(ethnicityId, age);

  const title = gender === 'male' ? 'Select his' : 'Select her';

  return (
    <ScreenEthnicityStyles>
      <OnboardingNavigation />

      <OwnAiTitle className="local-title">
        {title} <OwnAiTitleHighlight>ethnicity</OwnAiTitleHighlight>
      </OwnAiTitle>

      <IonContent>
        <OptionItemSpriteList>
          {ethnicityOptions.map((option) => (
            <OptionItemSprite
              key={option.id}
              alt="ethnicity"
              isSelected={ethnicity === option.id}
              onClick={() => handleSelect(option)}
              spriteSrc={getImageSrc(option.id)}
              title={option.title}
              TEST_ID={`ethnicity-${option.id}`}
              show
            />
          ))}
        </OptionItemSpriteList>
      </IonContent>

      <CompanyDetailsFooter extraPadding />
    </ScreenEthnicityStyles>
  );
};
